import React, { useState, useEffect, useCallback } from 'react';
import { Mail, MessageCircle, Save, Edit2, Info, Check, RefreshCw, FileText, Users } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const BUCKET_LABELS = {
  '3_7': '3-7 giorni',
  '7_14': '7-14 giorni',
  '14_30': '14-30 giorni',
  '30_plus': '30+ giorni',
  default: 'Standard (14-30gg)',
};

const ENTITY_LABELS = {
  quote: '📋 Preventivi',
  user: '👥 Utenti',
};

const CHANNEL_LABELS = {
  email: 'Email',
  whatsapp: 'WhatsApp',
};

const ENTITY_VARIABLES = {
  quote: [
    { key: 'first_name', desc: 'Nome cliente (es. "Marco")' },
    { key: 'customer_name', desc: 'Ragione sociale o nome completo' },
    { key: 'company_name', desc: 'Azienda cliente' },
    { key: 'quote_number', desc: 'Numero preventivo (es. "PREV-2026-001")' },
    { key: 'quote_total', desc: 'Totale preventivo formattato' },
    { key: 'admin_name', desc: 'Nome admin (es. "Davide")' },
  ],
  user: [
    { key: 'first_name', desc: 'Nome utente' },
    { key: 'customer_name', desc: 'Nome completo o ragione sociale' },
    { key: 'company_name', desc: 'Azienda' },
    { key: 'user_email', desc: 'Email utente' },
    { key: 'admin_name', desc: 'Nome admin' },
  ],
};

const AdminCrmTemplates = () => {
  usePageTitle('CRM Templates - Admin');
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template currently being edited
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/crm/funnel/templates`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const d = await res.json();
        setTemplates(d.items || []);
      }
    } catch (e) {
      console.error(e);
      toast({ title: 'Errore', description: 'Impossibile caricare i template', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [adminToken, apiUrl, toast]);

  useEffect(() => { load(); }, [load]);

  const grouped = templates.reduce((acc, t) => {
    const k = `${t.entity_type}|${t.age_bucket}`;
    if (!acc[k]) acc[k] = { entity_type: t.entity_type, age_bucket: t.age_bucket, items: [] };
    acc[k].items.push(t);
    return acc;
  }, {});

  // Ordering: quote first (3_7 -> 30_plus), then user (default -> 30_plus)
  const order = ['quote|3_7', 'quote|7_14', 'quote|14_30', 'quote|30_plus', 'user|default', 'user|30_plus'];
  const groups = order.map((k) => grouped[k]).filter(Boolean);

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="crm-templates-page">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <Edit2 className="w-6 h-6 mr-2 text-amber-600" />
              CRM Templates — Email & WhatsApp
            </h1>
            <p className="text-gray-600 mt-1 text-sm">Personalizza i messaggi automatici per preventivi e utenti dormienti</p>
          </div>
          <button
            onClick={load}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            data-testid="templates-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>

        {/* Info variabili */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm" data-testid="templates-info">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">Come usare le variabili</p>
              <p className="text-blue-800 mt-1">
                Scrivi <code className="bg-white px-1.5 py-0.5 rounded">{'{{nome_variabile}}'}</code> nel testo. Vengono sostituite automaticamente quando invii il messaggio.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-12">Caricamento template…</div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={`${g.entity_type}-${g.age_bucket}`} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-800">
                    {ENTITY_LABELS[g.entity_type]} — fascia <span className="text-amber-700">{BUCKET_LABELS[g.age_bucket]}</span>
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {g.items.map((t) => (
                    <TemplateRow
                      key={t.id}
                      template={t}
                      onEdit={() => setEditing(t)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <EditTemplateModal
            template={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
              toast({ title: 'Template salvato', description: 'Le modifiche sono attive da subito' });
            }}
            adminToken={adminToken}
            apiUrl={apiUrl}
          />
        )}
      </div>
    </AdminLayout>
  );
};

const TemplateRow = ({ template, onEdit }) => {
  const Icon = template.channel === 'email' ? Mail : MessageCircle;
  const colorClass = template.channel === 'email' ? 'text-blue-600 bg-blue-50' : 'text-green-600 bg-green-50';
  return (
    <div className="px-5 py-4 flex items-start gap-4 hover:bg-amber-50/30" data-testid={`template-row-${template.key}`}>
      <div className={`p-2 rounded-lg ${colorClass}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{CHANNEL_LABELS[template.channel]}</span>
          <span className="text-xs text-gray-400">{template.name}</span>
          {!template.is_active && (
            <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">Disattivo</span>
          )}
        </div>
        {template.subject && (
          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">Oggetto:</span> {template.subject}</p>
        )}
        <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">{template.body}</p>
      </div>
      <button
        onClick={onEdit}
        data-testid={`template-edit-${template.key}`}
        className="flex items-center px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
      >
        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
        Modifica
      </button>
    </div>
  );
};

const EditTemplateModal = ({ template, onClose, onSaved, adminToken, apiUrl }) => {
  const [name, setName] = useState(template.name || '');
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [isActive, setIsActive] = useState(template.is_active !== false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const isEmail = template.channel === 'email';
  const variables = ENTITY_VARIABLES[template.entity_type] || [];

  const insertVar = (key) => {
    setBody((b) => `${b}{{${key}}}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/api/crm/funnel/templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          name,
          subject: isEmail ? subject : undefined,
          body,
          is_active: isActive,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore salvataggio');
      }
      onSaved();
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="template-edit-modal">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-900">Modifica Template</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome interno</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="template-edit-name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {isEmail && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto email</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="template-edit-subject"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {isEmail ? 'Corpo email' : 'Messaggio WhatsApp'}
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={isEmail ? 10 : 6}
                data-testid="template-edit-body"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">Variabili disponibili (click per inserire)</p>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVar(v.key)}
                    title={v.desc}
                    data-testid={`template-var-${v.key}`}
                    className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-amber-50 hover:border-amber-400"
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                data-testid="template-edit-active"
                className="rounded"
              />
              <span>Attivo (usato quando invio dal funnel)</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="template-edit-save"
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm flex items-center disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCrmTemplates;
